export const CRM_ACTION_KEYS = [
  'crm.create_contact',
  'crm.update_contact',
  'crm.archive_contact',
  'crm.create_company',
  'crm.update_company',
  'crm.archive_company',
  'crm.create_deal',
  'crm.update_deal',
  'crm.archive_deal',
  'crm.log_activity',
  'crm.manage_segment',
  'crm.manage_schema',
  'email.send',
  'email.reply',
  'ai.bulk_research',
] as const

export type CrmActionKey = (typeof CRM_ACTION_KEYS)[number]

export type ActionDecision = 'auto' | 'approval' | 'disabled'

export type CrmObjectType = 'contact' | 'company' | 'deal'

export type FieldPermissions = Partial<Record<CrmObjectType, { deny: string[] }>>

export type CrmPermissions = {
  workspaceId: string
  actions: Record<CrmActionKey, ActionDecision>
  fieldPermissions: FieldPermissions
}

export type CrmPermissionsUpdateInput = {
  actions?: Partial<Record<CrmActionKey, ActionDecision>>
  fieldPermissions?: FieldPermissions
}

export const ACTION_LABELS: Record<CrmActionKey, string> = {
  'crm.create_contact': 'Create contacts',
  'crm.update_contact': 'Update contacts',
  'crm.archive_contact': 'Archive contacts',
  'crm.create_company': 'Create companies',
  'crm.update_company': 'Update companies',
  'crm.archive_company': 'Archive companies',
  'crm.create_deal': 'Create deals',
  'crm.update_deal': 'Update deals',
  'crm.archive_deal': 'Archive deals',
  'crm.log_activity': 'Log notes, emails & calls',
  'crm.manage_segment': 'Manage segments',
  'crm.manage_schema': 'Manage custom properties',
  'email.send': 'Send new emails',
  'email.reply': 'Reply to emails',
  'ai.bulk_research': 'Research many records at once',
}

export const ACTION_GROUPS: { title: string; keys: CrmActionKey[] }[] = [
  { title: 'Contacts', keys: ['crm.create_contact', 'crm.update_contact', 'crm.archive_contact'] },
  { title: 'Companies', keys: ['crm.create_company', 'crm.update_company', 'crm.archive_company'] },
  { title: 'Deals', keys: ['crm.create_deal', 'crm.update_deal', 'crm.archive_deal'] },
  { title: 'General', keys: ['crm.log_activity', 'crm.manage_segment', 'crm.manage_schema'] },
  { title: 'Email', keys: ['email.send', 'email.reply'] },
  { title: 'AI research', keys: ['ai.bulk_research'] },
]

export const OBJECT_TYPES: { type: CrmObjectType; label: string }[] = [
  { type: 'contact', label: 'Contacts' },
  { type: 'company', label: 'Companies' },
  { type: 'deal', label: 'Deals' },
]

export type FieldSuggestion = { key: string; label: string }

// The standard fields Zeph's CRM tools accept, by object type — these are the exact
// keys checked against the deny list (see apps/api .../chat/tools/executor.ts), which
// are Zeph's own tool-argument names, not necessarily HubSpot's raw internal property
// name (e.g. "job_title" here, "jobtitle" in HubSpot). Anything typed beyond this list
// is treated as a custom property's internal name instead.
export const FIELD_SUGGESTIONS: Record<CrmObjectType, FieldSuggestion[]> = {
  contact: [
    { key: 'first_name', label: 'First name' },
    { key: 'last_name', label: 'Last name' },
    { key: 'company', label: 'Company name' },
    { key: 'job_title', label: 'Job title' },
    { key: 'phone', label: 'Phone' },
    { key: 'lifecycle_stage', label: 'Lifecycle stage' },
    { key: 'lead_status', label: 'Lead status' },
    { key: 'owner_id', label: 'Owner' },
    { key: 'zeph_lead_score', label: 'Zeph lead score' },
    { key: 'zeph_qualification_status', label: 'Zeph qualification status' },
    { key: 'zeph_last_action', label: 'Zeph last action' },
  ],
  company: [
    { key: 'name', label: 'Company name' },
    { key: 'domain', label: 'Domain' },
    { key: 'industry', label: 'Industry' },
    { key: 'number_of_employees', label: 'Number of employees' },
    { key: 'annual_revenue', label: 'Annual revenue' },
    { key: 'city', label: 'City' },
    { key: 'country', label: 'Country' },
    { key: 'description', label: 'Description' },
    { key: 'linkedin_url', label: 'LinkedIn URL' },
  ],
  deal: [
    { key: 'deal_stage', label: 'Deal stage' },
    { key: 'amount', label: 'Deal amount' },
    { key: 'close_date', label: 'Close date' },
    { key: 'owner_id', label: 'Owner' },
    { key: 'deal_name', label: 'Deal name' },
  ],
}
